'use client';

import type { FormConfig, FormProviderProps } from '../../types/types';
import { FormProvider as HookFormProvider, useForm } from 'react-hook-form';
import React, {
	createContext,
	forwardRef,
	useContext,
	useImperativeHandle,
} from 'react';

import type { Ref } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const defaultConfigs = {
	disableSubmit: {
		onSubmitting:   true,
		onLoading:      true,
		whenIsNotDirty: false,
		whenNotValid:   true,
	},
};
const FormConfigContext = createContext<FormConfig>(defaultConfigs);

export const useFormConfigs = () => useContext(FormConfigContext);

// eslint-disable-next-line react/display-name
export const FormProvider = forwardRef(
	<T extends Record<string, unknown>>(
		props: FormProviderProps<T>,
		ref: React.Ref<UseFormReturn<T>>,
	) => {
		const {
			children,
			formConfigs = defaultConfigs,
			onSubmit,
			defaultValues,
			mode = 'onBlur',
			reValidateMode = 'onChange',
			schema,
		} = props;

		const methods = useForm<T>({
			defaultValues,
			mode,
			reValidateMode,
			resolver: zodResolver(schema),
		});

		useImperativeHandle(ref, () => methods, [ methods ]);

		return (
			<FormConfigContext.Provider value={formConfigs}>
				<HookFormProvider {...methods}>
					<form
						onReset={() => methods.reset(defaultValues)}
						onSubmit={methods.handleSubmit(onSubmit)}
					>
						{children}
					</form>
				</HookFormProvider>
			</FormConfigContext.Provider>
		);
	},
) as <T extends Record<string, unknown>>(
	props: FormProviderProps<T> & { ref?: Ref<UseFormReturn<T>> },
) => React.ReactNode;